import { useMemo, useState } from 'react';
import styles from './MarketHealth.module.css';
import { selectLag } from './strategyLagSelect';
import { MODE_HISTORY_FIELD } from './strategyTransitions';
import { toSortedPoints, parseDateMs } from './chartDateUtils';
import { RANGE_PRESETS } from './chartTheme';
import { computeWindowStats, buildFullStatRows } from './strategyStats';
import PerformanceStatsTable from './PerformanceStatsTable';
import InfoTip from '../common/InfoTip';


const MS_PER_DAY = 86400000;

const formatDate = (dateStr) => {
  if (typeof dateStr !== 'string') return '—';
  const parsed = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return dateStr;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

const formatSignedPct = (value) =>
  (Number.isFinite(value) ? `${value >= 0 ? '+' : ''}${value.toFixed(2)}%` : '—');

// The "Edge" column shows the STRATEGY'S benefit from sitting in cash
// — netEffectPct, the return differential vs. staying invested through the
// cash period (= -spyChangePct). This is what the reader actually cares about:
// "did being in cash help, and by how much." SPY fell while in cash -> that
// decline was avoided (positive net effect, good, ✓, green); SPY rose -> a
// missed gain / whipsaw (negative net effect, bad, ✗, red); exactly flat -> a
// true wash (neutral, –). Unlike the old "SPY While in Cash" framing, the
// number and glyph share the SAME sign and SAME color: a green ✓ sits next to
// a green +8.68%, a red ✗ next to a red -1.20%. They agree by construction, so
// they're colored together as one unit rather than independently.
const CHECK = '\u2713'; // ✓
const CROSS = '\u2717'; // ✗
const NEUTRAL = '\u2013'; // – (en dash)

// Verdict + color for the avoided-move (netEffectPct) value. Positive net
// effect (cash helped) = ✓ green; negative (whipsaw / missed gain) = ✗ red;
// flat = – neutral. The single className colors both the glyph and the number.
const avoidedMoveVerdict = (netEffectPct) => {
  if (!Number.isFinite(netEffectPct)) return { glyph: '', className: 'tradeHistoryCell' };
  const rounded = Number(netEffectPct.toFixed(2));
  if (rounded > 0) return { glyph: CHECK, className: 'tradeHistoryNetPositive' };
  if (rounded < 0) return { glyph: CROSS, className: 'tradeHistoryNetNegative' };
  return { glyph: NEUTRAL, className: 'tradeHistoryNetNeutral' };
};

const daysBetween = (startDate, endDate) => {
  const startMs = typeof startDate === 'string' ? parseDateMs(startDate) : NaN;
  const endMs = typeof endDate === 'string' ? parseDateMs(endDate) : NaN;
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  return Math.round((endMs - startMs) / MS_PER_DAY);
};

/**
 * Client-side fallback (spec §7): "If `strategy.responsive.trades` array is
 * not yet in the payload (requires backend change), compute the table
 * client-side from the `history` array's position transitions as an interim
 * approach." Scans the selected mode's position series oldest->newest and
 * emits one trade object per invested->cash->invested round trip, in the
 * same shape the backend's `tradeHistory` array carries (exitDate/entryDate/
 * exitScore/spyAtExit/spyAtEntry/spyChangePct/netEffectPct) so the render
 * path below never has to branch on where a trade came from.
 *
 * `netEffectPct` is the return differential vs. staying invested through the
 * cash period: the negative of `spyChangePct` — if SPY fell while the
 * strategy sat in cash, that decline was avoided (positive net effect); if
 * SPY rose, that gain was missed (negative net effect / whipsaw cost).
 */
export const collectTradesFromHistory = (history, modeField, lag = false) => {
  const sorted = toSortedPoints(history, 'spy').filter(
    (d) => selectLag(d[modeField], lag) && typeof selectLag(d[modeField], lag).position === 'string',
  );

  const trades = [];
  let pendingExit = null; // { exitDate, exitScore, spyAtExit }

  sorted.forEach((point, i) => {
    const prevPosition = i > 0 ? selectLag(sorted[i - 1][modeField], lag).position : null;
    const position = selectLag(point[modeField], lag).position;

    if (position === 'cash' && prevPosition === 'invested') {
      pendingExit = {
        exitDate: point.date,
        exitScore: typeof point.score === 'number' ? point.score : null,
        spyAtExit: point.spy,
      };
    } else if (position === 'invested' && prevPosition === 'cash' && pendingExit) {
      const spyAtEntry = point.spy;
      const spyChangePct = pendingExit.spyAtExit
        ? ((spyAtEntry - pendingExit.spyAtExit) / pendingExit.spyAtExit) * 100
        : null;
      trades.push({
        exitDate: pendingExit.exitDate,
        entryDate: point.date,
        exitScore: pendingExit.exitScore,
        spyAtExit: pendingExit.spyAtExit,
        spyAtEntry,
        spyChangePct,
        netEffectPct: Number.isFinite(spyChangePct) ? -spyChangePct : null,
      });
      pendingExit = null;
    }
  });

  return trades;
};

/**
 * Strategy Performance card (formerly "Trade History"). Renders, in order:
 *   1. a range/crash preset picker (RANGE_PRESETS) this card owns independently
 *      of the chart cards, so its stats/trades window updates on its own;
 *   2. the FULL strategy-vs-SPY stats table (return + risk rows, via
 *      buildFullStatRows) computed over the selected window (computeWindowStats)
 *      — this is the comparison table that used to sit under the S&P 500 chart
 *      as a fixed full-history summary; it now lives here and reacts to the
 *      picker;
 *   3. the "when this model lags" caveat callout (spec §5); and
 *   4. the completed-trades round-trip table (below).
 *
 * Trade table (spec §7): every historical position round-trip as a
 * row — Exit Date / Re-entry Date / Days in Cash / Edge —
 * sorted newest-first, and (for a crash/range preset) scoped to trades whose
 * exit falls in the selected window. The "Edge" column shows the
 * strategy's benefit from the cash period — netEffectPct, the return
 * differential vs. staying invested (= -spyMove) — prefixed with a verdict
 * glyph (✓ cash helped / ✗ whipsaw / – wash). This is the reader-facing
 * "did being in cash help, and by how much" number, so glyph and value share
 * one sign and one color — see avoidedMoveVerdict. (It replaces the earlier
 * "SPY While in Cash" framing, which showed SPY's raw move and colored the
 * glyph and price independently; that made the verdict glyph and the number
 * disagree — a green ✓ next to a red falling SPY — which read as noise.)
 *
 * Prefers the backend's `strategy[mode].tradeHistory` array when present
 * (even if empty — an explicit "no trades" answer from the backend is
 * trusted, not overridden). Falls back to `collectTradesFromHistory` —
 * computing the same shape client-side from `history`'s position
 * transitions — only when the backend hasn't shipped the field yet, per the
 * spec's interim-approach note. This means the component renders correctly
 * whether or not the `tradeHistory` array has been deployed.
 *
 * NOTE: the field is `tradeHistory`, not `trades` — despite spec §7's literal
 * text saying "Add a `trades` array". `strategy[mode].trades` is already a
 * different, pre-existing field: the numeric round-trip COUNT that spec §11 /
 * StrategyChart.js's lead line (`{modeStrategy.trades ?? 0} trades since...`)
 * renders live for this same mode. Naming the array `trades` too would
 * silently clobber that number with an array (React would then crash trying
 * to render an array of objects as a child). The backend
 * (market-health-compute/index.mjs) deliberately uses `tradeHistory` for the
 * array for exactly this reason — see its test coverage asserting `trades`
 * stays numeric and distinct from `tradeHistory`. Match that name here rather
 * than "fixing" it back to `trades`.
 */
const TradeHistory = ({ history = [], strategy, mode, lag = false, onLagChange }) => {
  const modeField = MODE_HISTORY_FIELD[mode] || MODE_HISTORY_FIELD.adaptive;
  const modeStrategy = selectLag(strategy?.[mode], lag) ?? null;

  // This card owns its OWN range/crash preset selection, independent of the
  // Strategy and Drawdown chart cards (spec: "the table then updates based on
  // the selected picker, but independently for each card"). Same RANGE_PRESETS
  // the charts use, so the crash-window semantics stay consistent app-wide.
  const [selectedPreset, setSelectedPreset] = useState(0);
  const activePreset = RANGE_PRESETS[selectedPreset];

  const sorted = useMemo(() => toSortedPoints(history, 'spy'), [history]);

  // The windowed slice this card's full stats table is computed over — filtered
  // to the selected crash/range window for `subset` presets, full history for
  // 'All'. Mirrors the charts' `windowed`/`paired` filtering so all three
  // cards agree on what "the 2020 COVID window" means.
  const windowed = useMemo(() => {
    if (!activePreset?.subset) return sorted;
    const [min, max] = activePreset.getExtremes();
    return sorted.filter((d) => d.ts >= min && d.ts <= max);
  }, [sorted, activePreset]);

  const backendTrades = modeStrategy?.tradeHistory;
  const allTrades = Array.isArray(backendTrades)
    ? backendTrades
    : collectTradesFromHistory(history, modeField, lag);

  // Scope the trade round-trips to the selected window too: for a `subset`
  // preset, keep trades whose EXIT falls inside the window (the exit is the
  // event that defines the trade's position in time). 'All' keeps every trade.
  const trades = useMemo(() => {
    if (!activePreset?.subset) return allTrades;
    const [min, max] = activePreset.getExtremes();
    return allTrades.filter((t) => {
      const exitMs = parseDateMs(t.exitDate);
      return Number.isFinite(exitMs) && exitMs >= min && exitMs <= max;
    });
  }, [allTrades, activePreset]);

  const sortedNewestFirst = [...trades].sort(
    (a, b) => parseDateMs(b.exitDate) - parseDateMs(a.exitDate),
  );

  // Full stat set (return + risk rows) from the backend's PRECOMPUTED per-preset
  // windows (strategy[mode][lag].windows[presetKey]) — daily-accurate for every
  // range preset. Falls back to recomputing from the weekly-downsampled
  // `windowed` slice for older payloads that predate the `.windows` field.
  const windowStats = strategy?.[mode]?.[lag ? 'lagOn' : 'lagOff']?.windows?.[activePreset?.key];
  const statRows = modeStrategy
    ? buildFullStatRows(windowStats ?? computeWindowStats(windowed, modeField, lag))
    : [];

  return (
    <div>
      <div className={styles.tradeHistoryHeaderRow}>
        <div className={styles.rangePresets} role="group" aria-label="Strategy performance date range">
          {RANGE_PRESETS.map((preset, index) => {
            const active = selectedPreset === index;
            return (
              <button
                key={preset.text}
                type="button"
                className={active ? styles.rangePresetButtonActive : styles.rangePresetButton}
                aria-pressed={active}
                onClick={() => setSelectedPreset(index)}
              >
                {preset.text}
              </button>
            );
          })}
        </div>

        {onLagChange && (
          <div className={styles.strategyControlsRow}>
            <label className={styles.lagToggle}>
              <input
                type="checkbox"
                className={styles.lagToggleInput}
                checked={lag}
                onChange={(e) => onLagChange(e.target.checked)}
                aria-label="1-day execution lag"
              />
              <span className={styles.lagToggleTrack} aria-hidden="true" />
              <span className={styles.lagToggleLabel}>1-day lag</span>
            </label>
            <InfoTip align="right" text="OFF = intraday price: uses the current market price when the signal fires during trading hours. ON = closing price: uses the official closing price, acts the next trading day." />
          </div>
        )}
      </div>

      {modeStrategy && (
        <PerformanceStatsTable
          rows={statRows}
          strategyLabel="Strategy"
          benchmarkLabel="SPY Buy & Hold"
          lead={(
            <p className={styles.strategyLead}>
              Roughly half SPY&apos;s worst drawdown, with comparable return over full cycles.{' '}
              <strong>{modeStrategy.trades ?? 0}</strong> trades since{' '}
              <strong>{modeStrategy.startDate ?? sorted[0]?.date}</strong>.{' '}
              <span className={styles.strategyLeadWindow}>Stats reflect the selected range.</span>
            </p>
          )}
        >
          <div className={styles.lagCallout}>
            <p>
              <strong>Strategy profile.</strong> In calm rising markets this strategy has trailed SPY by 1–6%
              per year for years at a stretch (e.g. 2012–2019). It offers little protection in crashes
              too fast for the signal to react. Its edge comes from slow-developing bear markets,
              which have occurred in every decade on record but whose timing and shape are unknowable
              in advance.
            </p>
          </div>
        </PerformanceStatsTable>
      )}

      <h3 className={styles.tradeHistoryHeading}>Completed trades</h3>
      {sortedNewestFirst.length === 0 ? (
        <p className={styles.statusMessage}>
          {activePreset?.subset
            ? 'No completed trades in the selected range.'
            : 'No completed trades yet — the strategy has held a single position since history began.'}
        </p>
      ) : (
        <div className={styles.tradeHistoryWrap}>
          <table className={styles.tradeHistoryTable}>
            <thead>
              <tr>
                <th className={styles.tradeHistoryHeadCell}>Exit Date</th>
                <th className={styles.tradeHistoryHeadCell}>Re-entry Date</th>
                <th className={styles.tradeHistoryHeadCell}>Days in Cash</th>
                <th className={styles.tradeHistoryHeadCell}>Edge</th>
              </tr>
            </thead>
            <tbody>
              {sortedNewestFirst.map((trade) => {
                const days = daysBetween(trade.exitDate, trade.entryDate);
                const { glyph, className } = avoidedMoveVerdict(trade.netEffectPct);
                const pct = formatSignedPct(trade.netEffectPct);
                return (
                  <tr key={`${trade.exitDate}-${trade.entryDate}`}>
                    <td className={styles.tradeHistoryCell}>{formatDate(trade.exitDate)}</td>
                    <td className={styles.tradeHistoryCell}>{formatDate(trade.entryDate)}</td>
                    <td className={styles.tradeHistoryCell}>{days ?? '—'}</td>
                    <td className={styles[className]}>{glyph ? `${glyph} ${pct}` : pct}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TradeHistory;
